import React, { useCallback, useEffect, useState } from "react";
import { DiscardChangesPrompt } from "./DirtyDialogBlocker";

/**
 * X, Cancel, Escape, and backdrop handling for a routed dialog with a dirty form: asks before
 * discarding when `isDirty`, otherwise closes right away. The routed hook's own `discardPrompt`
 * covers Back and refresh; this covers the dialog's own close controls.
 */
export const useDiscardGuardedClose = ({
  open,
  isDirty,
  onClose,
}: {
  open: boolean;
  isDirty: boolean;
  onClose: () => void;
}): { requestClose: () => void; discardPrompt: React.ReactElement } => {
  const [promptOpen, setPromptOpen] = useState(false);

  useEffect(() => {
    if (!open) setPromptOpen(false);
  }, [open]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setPromptOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose]);

  const handleDiscard = useCallback(() => {
    setPromptOpen(false);
    onClose();
  }, [onClose]);

  return {
    requestClose,
    discardPrompt: (
      <DiscardChangesPrompt
        open={promptOpen}
        onKeepEditing={() => setPromptOpen(false)}
        onDiscard={handleDiscard}
      />
    ),
  };
};
